import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Edit, Building2, Clock, Calendar, Upload, FileText, Trash2, ExternalLink, StickyNote, Paperclip, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value || 0);
const getDaysInStage = (opp) => {
  const stageDate = opp.stage_entered_at || opp.created_at;
  if (!stageDate) return 0;
  return Math.floor((new Date() - new Date(stageDate)) / (1000 * 60 * 60 * 24));
};
const POLICY_COLORS = {
  'Critical Injury': 'bg-[#CCFBF1] text-[#0D9488]',
  'Performance Bonus': 'bg-[#FEF3C7] text-[#D97706]',
  'Transfer Insurance': 'bg-[#EEF0FF] text-[#6B5CE7]',
  'Consulting': 'bg-[#F1F5F9] text-[#475569]',
};
const EXT_BADGE = {
  pdf: 'bg-[#FEE2E2] text-[#DC2626]', html: 'bg-[#FEF3C7] text-[#D97706]', htm: 'bg-[#FEF3C7] text-[#D97706]',
};
const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-[#94A3B8] font-semibold uppercase tracking-wider mb-0.5">{label}</p>
    <p className="text-sm text-[#0F172A]">{value || <span className="text-[#CBD5E1]">—</span>}</p>
  </div>
);

const OpportunityDetailDialog = ({ opp, open, onClose, onEdit, onChanged }) => {
  const { getAuthHeaders, token } = useAuth();
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileDesc, setFileDesc] = useState('');
  const [noteSummary, setNoteSummary] = useState('');
  const [noteWaiting, setNoteWaiting] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const loadFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/opportunities/${opp.id}/attachments`, { headers: getAuthHeaders() });
      setFiles(res.data);
    } catch { /* ignore */ }
  }, [opp.id, getAuthHeaders]);

  const loadNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/opportunities/${opp.id}/notes`, { headers: getAuthHeaders() });
      setNotes(res.data);
    } catch { /* ignore */ }
  }, [opp.id, getAuthHeaders]);

  useEffect(() => { loadFiles(); loadNotes(); }, [loadFiles, loadNotes]);

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    if (!fileName) setFileName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!pendingFile) { toast.error('Choose a file first'); return; }
    if (!fileName.trim()) { toast.error('Enter a file name'); return; }
    const fd = new FormData();
    fd.append('file', pendingFile);
    fd.append('name', fileName.trim());
    fd.append('description', fileDesc.trim());
    setUploading(true);
    try {
      await axios.post(`${API}/opportunities/${opp.id}/attachments`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File attached');
      setPendingFile(null); setFileName(''); setFileDesc('');
      await loadFiles();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm('Remove this file from the deal?')) return;
    try {
      await axios.delete(`${API}/attachments/${id}`, { headers: getAuthHeaders() });
      setFiles(prev => prev.filter(f => f.id !== id));
      onChanged?.();
    } catch { toast.error('Failed to delete file'); }
  };

  const openFile = (f) => {
    window.open(`${API}/attachments/${f.id}/download?auth=${token}`, '_blank');
  };

  const addNote = async () => {
    if (!noteSummary.trim()) { toast.error('Describe where the deal sits this week'); return; }
    setSavingNote(true);
    try {
      await axios.post(`${API}/opportunities/${opp.id}/notes`, { summary: noteSummary.trim(), waiting_on: noteWaiting.trim() }, { headers: getAuthHeaders() });
      setNoteSummary(''); setNoteWaiting('');
      await loadNotes();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/opportunities/${opp.id}/notes/${id}`, { headers: getAuthHeaders() });
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch { toast.error('Failed to delete note'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="opp-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            <Badge className={`${POLICY_COLORS[opp.policy_type] || 'bg-[#F1F5F9] text-[#475569]'} border-0`}>{opp.policy_type || 'Policy'}</Badge>
            {opp.athlete_name || 'TBD'}
          </DialogTitle>
        </DialogHeader>

        {/* Header summary */}
        <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center"><Building2 className="w-5 h-5 text-[#007AFF]" /></div>
            <div>
              <p className="font-bold text-[#0F172A]">{opp.account?.university_name}</p>
              <p className="text-xs text-[#475569]">{opp.sport}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{formatCurrency(opp.total_estimated_premium || 0)}</p>
            <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Premium</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Badge variant="outline" className="border-[#E2E8F0] text-[#475569] bg-[#F8FAFC]">{opp.stage}</Badge>
          <Badge className="bg-[#FFF7ED] text-[#D97706] border-0 font-mono text-xs"><Clock className="w-3 h-3 mr-1" />{getDaysInStage(opp)}d in stage</Badge>
          <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0">{opp.status}</Badge>
          {opp.is_enterprise && <Badge className="bg-[#7C3AED] text-white border-0">ENTERPRISE</Badge>}
        </div>

        <Tabs defaultValue="details" className="mt-4">
          <TabsList className="bg-[#F1F5F9]">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">
              <Paperclip className="w-3.5 h-3.5 mr-1" /> Files {files.length > 0 && <span className="ml-1 text-[#007AFF]">({files.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">
              <StickyNote className="w-3.5 h-3.5 mr-1" /> Notes {notes.length > 0 && <span className="ml-1 text-[#007AFF]">({notes.length})</span>}
            </TabsTrigger>
          </TabsList>

          {/* DETAILS */}
          <TabsContent value="details" className="mt-4 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Owner" value={opp.owner?.name} />
              <Field label="Policy Type" value={opp.policy_type} />
              <Field label="Department" value={opp.department} />
              <Field label="Expected Close" value={opp.expected_close_date} />
              <Field label="Timing" value={opp.timing} />
              <Field label="Authority" value={opp.authority} />
              <Field label="Budget" value={opp.budget} />
              <Field label="Contact" value={opp.contact} />
            </div>
            {opp.support_team?.length > 0 && <Field label="Support / Biz Dev" value={opp.support_team.map(u => u.name).join(', ')} />}
            {opp.notes && (
              <div>
                <p className="text-[10px] text-[#94A3B8] font-semibold uppercase tracking-wider mb-1">Deal Notes</p>
                <p className="text-sm text-[#475569] whitespace-pre-wrap bg-[#F8FAFC] p-3 rounded-lg border border-[#E2E8F0]">{opp.notes}</p>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
              <Calendar className="w-3.5 h-3.5" /> Created {opp.created_at ? new Date(opp.created_at).toLocaleDateString() : '—'}
            </div>
          </TabsContent>

          {/* FILES */}
          <TabsContent value="files" className="mt-4 space-y-4">
            <div className="p-4 bg-[#F8FAFC] rounded-xl border border-dashed border-[#CBD5E1] space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#334155]">
                <Upload className="w-4 h-4 text-[#007AFF]" /> Attach signed agreement or bind order
              </div>
              <input id="opp-file-input" type="file" accept=".pdf,.html,.htm,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={onFilePicked} className="text-sm text-[#475569] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#007AFF] file:text-white file:text-sm file:font-medium hover:file:bg-[#0066CC] file:cursor-pointer" data-testid="file-input" />
              {pendingFile && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#475569]">File name</Label>
                      <Input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="e.g. Bind Order" className="bg-white border-[#E2E8F0] text-[#0F172A] h-9" data-testid="file-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#475569]">Description</Label>
                      <Input value={fileDesc} onChange={(e) => setFileDesc(e.target.value)} placeholder="Short description" className="bg-white border-[#E2E8F0] text-[#0F172A] h-9" data-testid="file-desc-input" />
                    </div>
                  </div>
                  <Button onClick={handleUpload} disabled={uploading} className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold w-full" data-testid="file-upload-btn">
                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload {pendingFile.name}</>}
                  </Button>
                </div>
              )}
            </div>

            {files.length === 0 ? (
              <div className="text-center py-8 text-[#94A3B8] text-sm">
                <FileText className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
                No files attached yet
              </div>
            ) : (
              <div className="space-y-2" data-testid="file-list">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-3 p-3 bg-white border border-[#E2E8F0] rounded-lg hover:border-[#007AFF]/40 transition-colors" data-testid={`file-item-${f.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#475569]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#0F172A] truncate">{f.name}</p>
                          <Badge className={`${EXT_BADGE[f.file_ext] || 'bg-[#F1F5F9] text-[#475569]'} border-0 text-[10px] uppercase`}>{f.file_ext}</Badge>
                        </div>
                        {f.description && <p className="text-xs text-[#94A3B8] truncate">{f.description}</p>}
                        <p className="text-[11px] text-[#CBD5E1]">{fmtSize(f.size)} · {fmtDate(f.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openFile(f)} className="h-8 w-8 text-[#475569] hover:text-[#007AFF] hover:bg-[#EFF6FF]" data-testid={`file-open-${f.id}`}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteFile(f.id)} className="h-8 w-8 text-[#475569] hover:text-[#FF3B30] hover:bg-[#FEF2F2]" data-testid={`file-delete-${f.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* NOTES */}
          <TabsContent value="notes" className="mt-4 space-y-4">
            <div className="p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-[#475569] uppercase tracking-wider font-medium">Where does this deal sit this week?</Label>
                <Textarea value={noteSummary} onChange={(e) => setNoteSummary(e.target.value)} placeholder="e.g. Verbal agreement reached, drafting bind order" className="bg-white border-[#E2E8F0] text-[#0F172A] min-h-[60px]" data-testid="note-summary-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#475569] uppercase tracking-wider font-medium">What's it waiting on?</Label>
                <Input value={noteWaiting} onChange={(e) => setNoteWaiting(e.target.value)} placeholder="e.g. Athlete signature, AD approval" className="bg-white border-[#E2E8F0] text-[#0F172A] h-9" data-testid="note-waiting-input" />
              </div>
              <Button onClick={addNote} disabled={savingNote} className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold w-full" data-testid="note-add-btn">
                {savingNote ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><StickyNote className="w-4 h-4 mr-2" /> Add Weekly Note</>}
              </Button>
            </div>

            {notes.length === 0 ? (
              <div className="text-center py-8 text-[#94A3B8] text-sm">
                <StickyNote className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
                No notes yet — add your first weekly status update
              </div>
            ) : (
              <div className="space-y-3" data-testid="note-list">
                {notes.map((n) => (
                  <div key={n.id} className="p-3 bg-white border border-[#E2E8F0] rounded-lg border-l-4 border-l-[#007AFF]" data-testid={`note-item-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-[#0F172A] whitespace-pre-wrap flex-1">{n.summary}</p>
                      <Button variant="ghost" size="icon" onClick={() => deleteNote(n.id)} className="h-6 w-6 text-[#CBD5E1] hover:text-[#FF3B30] hover:bg-[#FEF2F2] flex-shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {n.waiting_on && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-[#D97706]">
                        <AlertCircle className="w-3.5 h-3.5" /> Waiting on: <span className="font-medium">{n.waiting_on}</span>
                      </div>
                    )}
                    <p className="text-[11px] text-[#94A3B8] mt-2">{n.author_name || 'Unknown'} · {fmtDate(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex gap-3 pt-4 mt-2 border-t border-[#E2E8F0]">
          <Button variant="outline" onClick={onClose} className="flex-1 border-[#E2E8F0] text-[#475569]">Close</Button>
          <Button onClick={() => onEdit(opp)} className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold" data-testid="detail-edit-btn">
            <Edit className="w-4 h-4 mr-2" /> Edit Policy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OpportunityDetailDialog;
